import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Paper, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { IRoadLayoutConfig, ILightingClassConfig, ILightingClassConfigCallBack, TooltipData } from "types";
import { APP_COMPANY_NAME } from "consts";
import { CarriagewayImage } from "components/CarriagewayImage";
import { CustomClassModal } from "./components/CustomClassModal";
import { CustomClassTable } from "./components/CustomClassTable";
import ClassInformation from "./components/ClassInformation";
import ClassOptions from "./components/ClassOptions";
import BorderClasses from "./components/BorderClasses";
import { useLightingClassConfig } from "./hooks/useLightingClassConfig";
import getStyles from "./styles";

type LightingClassProps = {
  roadLayoutConfig: IRoadLayoutConfig;
  lightingClassConfig: ILightingClassConfig;
  onLightingClassConfig: ILightingClassConfigCallBack;
  tooltips: TooltipData | null;
};

const LightingClass: FC<LightingClassProps> = ({ roadLayoutConfig, lightingClassConfig, onLightingClassConfig, tooltips }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down("md"));
  const styles = useMemo(() => getStyles(theme), [theme]);
  const language = i18n.resolvedLanguage;

  const { borderAreaLeft, borderAreaRight } = roadLayoutConfig;
  const { roadClasses, customClasses } = lightingClassConfig;

  const { isOpen, road, customClassData, preSelectedClass, closeModal, handleLightingClassConfig, openModalForSpecificRoad, handleSaveDataFromModal } =
    useLightingClassConfig(lightingClassConfig, onLightingClassConfig);

  return (
    <>
      <Box sx={styles.container}>
        <CarriagewayImage roadLayoutConfig={roadLayoutConfig} sx={styles.image} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={styles.header}>
          <Typography variant="h2" sx={styles.title}>
            {t("Road Classes")}
          </Typography>
          {APP_COMPANY_NAME.toLowerCase() === "signify" && language === "de" && (
            <Button component="a" size="small" variant="contained" href="https://class-assistant.relux.com/" target="_blank" sx={styles.assistant}>
              {t("Lighting Class Selection")}
            </Button>
          )}
        </Stack>

        <ClassInformation tooltips={tooltips} />

        <Paper sx={styles.wrapper}>
          <Stack sx={styles.classes}>
            <ClassOptions classType="M" name="RoadMClasses" selected={roadClasses} onChange={handleLightingClassConfig} />
            <ClassOptions classType="C" name="RoadCClasses" selected={roadClasses} onChange={handleLightingClassConfig} />
            <ClassOptions classType="P" name="RoadPClasses" selected={roadClasses} onChange={handleLightingClassConfig} />
          </Stack>

          <CustomClassTable data={customClasses?.mainCustomClass} sx={styles.custom} />

          <Stack component="footer" sx={styles.actions}>
            <Button variant={md ? "text" : "contained"} onClick={() => openModalForSpecificRoad("main")}>
              {t("Custom Class")}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ justifyContent: borderAreaLeft && borderAreaRight ? "space-between" : "stretch", ...styles.borders }}>
          {borderAreaLeft && (
            <BorderClasses
              border="leftBorder"
              roadLayoutConfig={roadLayoutConfig}
              lightingClassConfig={lightingClassConfig}
              onLightingClassConfig={handleLightingClassConfig}
              onOpenModal={openModalForSpecificRoad}
            />
          )}
          {borderAreaRight && (
            <BorderClasses
              border="rightBorder"
              roadLayoutConfig={roadLayoutConfig}
              lightingClassConfig={lightingClassConfig}
              onLightingClassConfig={handleLightingClassConfig}
              onOpenModal={openModalForSpecificRoad}
            />
          )}
        </Box>
      </Box>

      <CustomClassModal
        open={isOpen}
        road={road}
        preSelectedClass={preSelectedClass}
        customClassData={customClassData}
        handleClose={closeModal}
        handleSaveDataFromModal={handleSaveDataFromModal}
      />
    </>
  );
};

export default LightingClass;
